import React, { useEffect, useState, useContext } from 'react'
import PropTypes from 'prop-types'

import {
  RatingFilter, AllReviewRatingSelectors, type RatingSelections,
  ReviewFilters,
  SharedWithSupplierFilter,
  SupplierReplyFilter, type ReplySelectionMapKey, SharedWithSupplierSelectionMapKey
} from 'components/review_tiles/filters'
import { AuthContext, verifiedAgencyUser, AuthState } from 'components/common/auth/AuthContext'

const ReplyFilterMap = {
  all: {},
  withReplies: { replied_to: true },
  withoutReplies: { replied_to: false }
} as const
type ReplyFilterMapKey = keyof typeof ReplyFilterMap
type ReplyFilterMapValue = typeof ReplyFilterMap[ReplyFilterMapKey]

const SharedWithSupplierFilterMap = {
  all: {},
  shared: { shared: true },
  notShared: { shared: false }
} as const
type SharedWithSupplierFilterMapKey = keyof typeof SharedWithSupplierFilterMap
type SharedWithSupplierFilterMapValue = typeof SharedWithSupplierFilterMap[SharedWithSupplierFilterMapKey]

interface RatingSelectionParam {
  stars: RatingSelections
}

export type EmptyReviewFilterParams = Record<never, never>

export interface ReviewFilterContainerParams {
  reply_to?: boolean
  shared?: boolean
  stars?: RatingSelections
}

interface ReviewFiltersContainerProps {
  handleFiltersQuery: (query: ReviewFilterContainerParams) => void
}

const ratingSelectionsToParam = (selections: RatingSelections): RatingSelectionParam => {
  return { stars: selections }
}

export const ReviewFiltersContainer: React.FC<ReviewFiltersContainerProps> = ({ handleFiltersQuery }) => {
  const [replyParams, setReplyParams] = useState<ReplyFilterMapValue>(ReplyFilterMap.all)
  const [sharedParams, setSharedParams] = useState<SharedWithSupplierFilterMapValue>(SharedWithSupplierFilterMap.all)
  const [ratings, setRatings] = useState<RatingSelections>(AllReviewRatingSelectors)

  const handleReplyFilterChange = (selection: ReplySelectionMapKey): void => {
    const replySelection = (selection in ReplyFilterMap) ? selection : 'all'
    setReplyParams(ReplyFilterMap[replySelection])
  }

  const handleSharedWithSupplierFilterChange = (selection: SharedWithSupplierSelectionMapKey): void => {
    const shareSelection = (selection in SharedWithSupplierFilterMap) ? selection : 'all'
    setSharedParams(SharedWithSupplierFilterMap[shareSelection])
  }

  const handleRatingFilterChange = (selections: RatingSelections): void => {
    setRatings(selections.length === 0 ? AllReviewRatingSelectors : selections)
  }

  const authContext = useContext<AuthState>(AuthContext)

  const agencyUserSharedWithSupplierFilter = verifiedAgencyUser(authContext)
    ? (<SharedWithSupplierFilter key='1' onChange={handleSharedWithSupplierFilterChange} />)
    : null

  const dropdownFilters = [
    <SupplierReplyFilter key='0' onChange={handleReplyFilterChange} />,
    agencyUserSharedWithSupplierFilter,
    <RatingFilter key='2' onChange={handleRatingFilterChange} />
  ].filter(Boolean) as React.ReactElement[]

  const reviewFilterParams = (): ReviewFilterContainerParams => {
    return { ...replyParams, ...sharedParams, ...ratingSelectionsToParam(ratings) }
  }

  useEffect(() => {
    handleFiltersQuery(reviewFilterParams())
  }, [replyParams, sharedParams, ratings])

  return (
    <div className='canary-review-filters'>
      <ReviewFilters dropdownFilters={dropdownFilters} />
    </div>
  )
}

ReviewFiltersContainer.propTypes = {
  handleFiltersQuery: PropTypes.func.isRequired
}
